import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { trackSignIn } from "@/client/analytics/mixpanel";
import { env } from "@/client/config/env";
import { clerkAppearance } from "@/client/libs/clerk";
import { markClerkReady, setClerkTokenGetter } from "@/client/libs/clerk-token";
import { queryClient } from "@/client/libs/query-client";
import { fetchUser } from "@/client/services/user/user";
import { useAuthStore } from "@/client/stores/auth";
import { isFirstRoundMarketingHost } from "@/client/utils/firstRoundAppUrl";

// Validated (required) by the env schema — the app fails fast at boot if it's missing.
const publishableKey = env.VITE_CLERK_PUBLISHABLE_KEY;

/** Registers Clerk's token getter with the axios bridge and signals when Clerk has loaded. */
const ClerkTokenBridge = () => {
  const { getToken, isLoaded } = useAuth();

  useEffect(() => {
    setClerkTokenGetter(() => getToken());
  }, [getToken]);

  useEffect(() => {
    if (isLoaded) markClerkReady();
  }, [isLoaded]);

  return null;
};

/**
 * Keeps the persisted `auth` store in lockstep with the Clerk session on EVERY route.
 *
 * Before this existed, the store was only ever written by `useUser()` — which is mounted from
 * AuthGuard and dashboard components. Public routes (the job share page, the marketing header)
 * therefore read a localStorage-persisted `user` that could be arbitrarily stale:
 *   - signed out / session expired, store still holding a user  → UI acts signed in, every API
 *     call comes back 401 "Missing Clerk session token";
 *   - just signed in via Clerk and bounced back to a public page → store still null, UI keeps
 *     asking the candidate to sign in.
 *
 * Shares the ["user"] query key with `useUser()`, so AuthGuard reads this from cache instead of
 * refetching.
 */
const ClerkSessionSync = () => {
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const setUser = useAuthStore((state) => state.setUser);

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUser,
    enabled: isLoaded && !!isSignedIn,
  });

  useEffect(() => {
    // Clerk hasn't resolved the session yet — leave whatever is there rather than flashing
    // signed-out UI at a signed-in candidate.
    if (!isLoaded) return;

    if (!isSignedIn) {
      // Clerk is authoritative: no session means no user, whatever localStorage remembers.
      if (useAuthStore.getState().user) setUser(null);
      queryClient.setQueryData(["user"], null);
      return;
    }

    if (user) setUser(user);
  }, [isLoaded, isSignedIn, user, setUser]);

  // signed_in, once per new Clerk session (mixpanel.ts trackSignIn).
  useEffect(() => {
    if (isLoaded && isSignedIn && sessionId && user) trackSignIn(sessionId, user);
  }, [isLoaded, isSignedIn, sessionId, user]);

  // Just signed in. While signed out the query cache holds a fresh "no user" (queries have a
  // 1-minute staleTime and don't refetch on mount), so without this the app kept treating a
  // signed-in visitor as signed out for up to a minute: guards sent them to sign-in, sign-in sent
  // them back, and the history filled with bounces.
  useEffect(() => {
    if (isLoaded && isSignedIn && !queryClient.getQueryData(["user"])) {
      void queryClient.invalidateQueries({ queryKey: ["user"] });
    }
  }, [isLoaded, isSignedIn]);

  return null;
};

/**
 * The First Round marketing hosts never sign anyone in, and Clerk's production instance rejects
 * firstround.com.au, so Clerk is not loaded there at all. The axios bridge still waits for Clerk to
 * report ready before every request (up to 4s), so signal ready straight away: requests from these
 * pages (the Practice Interview demo) are anonymous.
 */
const WithoutClerk = ({ children }: { children: ReactNode }) => {
  useEffect(() => {
    markClerkReady();
  }, []);

  return children;
};

const WithClerk = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={clerkAppearance}
      // Leaving a sign-in / sign-up page replaces it in history. Otherwise Back returns to the
      // auth page, which (now signed in) sends you straight forward again: a back-button trap.
      routerPush={(to) => navigate(to, { replace: window.location.pathname.startsWith("/auth/") })}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/auth/login"
      signUpUrl="/auth/register"
    >
      <ClerkTokenBridge />
      <ClerkSessionSync />
      {children}
    </ClerkProvider>
  );
};

/**
 * Wraps the app in Clerk, wired to react-router navigation and theResume's brand appearance —
 * except on the First Round marketing hosts, which run without Clerk.
 */
export const ClerkAppProvider = ({ children }: { children: ReactNode }) =>
  isFirstRoundMarketingHost() ? (
    <WithoutClerk>{children}</WithoutClerk>
  ) : (
    <WithClerk>{children}</WithClerk>
  );
